import { FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { imageSrc } from "@/lib/site";

export interface ReaderPhoto { id: string; post_slug: string; post_title: string; first_name: string; note: string; image: string; created_at: string }

export function PhotoGrid({ photos, showPost = false }: { photos: ReaderPhoto[]; showPost?: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="reader-photo-grid">
      {photos.map((p) => (
        <figure key={p.id} className="calm-card overflow-hidden" data-testid={`reader-photo-${p.id}`}>
          <img src={imageSrc(p.image)} alt={`${p.first_name}'s ${p.post_title}`} className="aspect-square w-full object-cover" loading="lazy" />
          <figcaption className="p-3 text-sm text-[#23324A]">
            <b>{p.first_name}</b>{p.note && <span className="text-[#23324A]/75">: {p.note}</span>}
            {showPost && <Link to={`/${p.post_slug}`} className="mt-1 block text-xs font-semibold text-[#D9785C] hover:underline">{p.post_title}</Link>}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function SubmitForm({ slug }: { slug: string }) {
  const file = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [consent, setConsent] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const f = file.current?.files?.[0];
    if (!f) { setErr("Please choose a photo"); return; }
    const form = new FormData();
    form.append("file", f); form.append("first_name", name); form.append("note", note); form.append("consent", String(consent));
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/public/posts/${slug}/photos`, form, { timeout: 120000 }); setMsg(data.message); }
    catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };
  if (msg) return <p className="mt-4 rounded-xl bg-white px-4 py-3 font-semibold text-[#23324A]" data-testid="photo-submit-success">{msg}</p>;
  return (
    <form onSubmit={submit} className="mt-4 space-y-3" data-testid="photo-submit-form">
      <input ref={file} type="file" accept="image/*" required className="block w-full text-sm" data-testid="photo-file-input" />
      <div className="grid gap-3 sm:grid-cols-2">
        <input className="field bg-white" required maxLength={40} placeholder="First name" value={name} onChange={(e) => setName(e.target.value)} data-testid="photo-name-input" />
        <input className="field bg-white" maxLength={200} placeholder="A short note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="photo-note-input" />
      </div>
      <label className="flex items-start gap-2 text-sm text-[#23324A]">
        <input type="checkbox" className="mt-1" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="photo-consent-check" />
        I made this, it's my photo, and you can show it on this site. It doesn't show any children's faces.
      </label>
      {err && <p className="rounded-xl bg-[#FDE6DF] px-3 py-2 text-sm font-semibold text-[#23324A]" data-testid="photo-submit-error">{err}</p>}
      <button className="btn-solid" disabled={busy || !consent} type="submit" data-testid="photo-submit-btn"><Camera className="h-4 w-4" />{busy ? "Sending…" : "Share my photo"}</button>
    </form>
  );
}

export function ReaderPhotos({ slug }: { slug: string }) {
  const [photos, setPhotos] = useState<ReaderPhoto[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get(`/public/posts/${slug}/photos`).then((r) => setPhotos(r.data)).catch(() => {}); }, [slug]);
  return (
    <section className="mt-14" data-testid="reader-photos">
      <h2 className="font-heading text-xl font-semibold text-[#23324A]">Made by readers</h2>
      {photos.length > 0 && <div className="mt-4"><PhotoGrid photos={photos} /></div>}
      <div className="calm-card mt-5 p-6">
        <p className="font-heading text-lg font-semibold text-[#23324A]">Made one? I'd love to see it.</p>
        <p className="mt-1 text-sm text-[#23324A]/75">Share a photo of your finished craft. I look at every photo before it goes up. Please keep children's faces out of the picture.</p>
        {open ? <SubmitForm slug={slug} /> : <button className="btn-line mt-4 bg-white" onClick={() => setOpen(true)} data-testid="photo-share-open-btn"><Camera className="h-4 w-4" />Share your photo</button>}
      </div>
    </section>
  );
}
